import { BASELINE_WEEK_LENGTH } from '@galapagos/shared';
import { DateTime } from 'luxon';
import { labelLocale } from '../wrap-up/booking-slots';

/** Luxon's ISO weekday: 1 is Monday, 7 is Sunday. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Monday to Sunday, the order the summary and the usual-week rows use. */
export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7];

/** One of the seven days before the anchor, as the member sees it. */
export interface ReportedDay {
  /** 1 is the day before the anchor ("yesterday"), 7 a week before it. */
  ordinal: number;
  /** `YYYY-MM-DD` in the member's zone. */
  date: string;
  weekday: Weekday;
  /** "Sunday"; "Domingo" in Spanish. */
  weekdayLabel: string;
  /** "Oct 11"; "11 oct" in Spanish. */
  shortDate: string;
}

function isWeekday(value: number): value is Weekday {
  return Number.isInteger(value) && value >= 1 && value <= 7;
}

function calendarDay(date: string, zone: string, language: string): DateTime {
  const day = DateTime.fromISO(date, { zone }).setLocale(labelLocale(language));
  if (!day.isValid) throw new RangeError('Not a calendar date');
  return day;
}

/**
 * A weekday name with its first letter capitalised. Spanish writes weekday
 * names lowercase mid-sentence, but here each one stands alone as a row or
 * column label, beside a capitalised "Ayer".
 */
function weekdayName(day: DateTime, format: 'cccc' | 'ccc'): string {
  const name = day.toFormat(format);
  return name.charAt(0).toLocaleUpperCase(day.locale ?? undefined) + name.slice(1);
}

function weekdayOf(day: DateTime): Weekday {
  const { weekday } = day;
  if (!isWeekday(weekday)) throw new RangeError('Not a weekday');
  return weekday;
}

/**
 * Today's date in the member's zone: the day the week counts back from. A
 * member in their evening is still on their own date, whatever UTC says.
 */
export function baselineAnchor(now: DateTime, zone: string): string {
  const date = now.setZone(zone).toISODate();
  if (date === null) throw new RangeError('The current instant is not valid');
  return date;
}

/** The seven days before `anchorDate`, most recent first, labelled in `language`. */
export function reportedDays(anchorDate: string, zone: string, language = 'en'): ReportedDay[] {
  const anchor = calendarDay(anchorDate, zone, language);
  return Array.from({ length: BASELINE_WEEK_LENGTH }, (_, index) => {
    const ordinal = index + 1;
    const day = anchor.minus({ days: ordinal });
    return {
      ordinal,
      date: day.toFormat('yyyy-MM-dd'),
      weekday: weekdayOf(day),
      weekdayLabel: weekdayName(day, 'cccc'),
      // The locale's own month-day order: "Oct 11" in English, "11 oct" in Spanish.
      shortDate: day.toLocaleString({ month: 'short', day: 'numeric' }),
    };
  });
}

/**
 * Which day ordinal falls on `weekday` for the week before `anchorDate`:
 * the usual-week answer for that weekday is saved under the same ordinal, so
 * the server dates it the same way it dates the reported day.
 */
export function ordinalOnWeekday(anchorDate: string, zone: string, weekday: Weekday): number {
  const day = reportedDays(anchorDate, zone).find((candidate) => candidate.weekday === weekday);
  if (!day) throw new RangeError('Every weekday falls once in a week');
  return day.ordinal;
}

/** "Monday" and "Mon" for a weekday in `language`, independent of any date. */
export function weekdayLabels(weekday: Weekday, language = 'en'): { long: string; short: string } {
  // A known Monday, read as a calendar date: only its weekday matters.
  const day = DateTime.fromISO('2026-10-12', { zone: 'utc' })
    .setLocale(labelLocale(language))
    .plus({ days: weekday - 1 });
  return { long: weekdayName(day, 'cccc'), short: weekdayName(day, 'ccc') };
}
